import { useCallback, useEffect, useState } from 'react';

import {
  createProgramUnitInstance,
  fetchAssignedScreeners,
  type ProgramUnitInstance,
} from '@/modules/api';
import type { SurveyQuestion } from '@/modules/survey';

export type ScreenerContent = {
  /** Stable id for this screener instance — use as a React key to reset survey state. */
  programUnitInstanceUri: string;
  questionGroupUri: string;
  questions: SurveyQuestion[];
  /** The screener's internalName (Sparta's `screener_name`, for analytics). */
  internalName: string;
};

type UseAssignedScreener = {
  content: ScreenerContent | undefined;
  isLoading: boolean;
  /** True once the endpoint returns no screeners — the dynamic survey is done. */
  isExhausted: boolean;
  error: unknown;
  /** Re-fetch the list + start the next screener (call after submitting answers). */
  next: () => void;
};

export type UseAssignedScreenerOptions = {
  /**
   * Screener keys to exclude from the fetch via `exclude_screeners`. Pass the
   * AOM legacy keys when this runs after the dynamic AOM flows (test arm) so the
   * already-answered AOM screeners aren't re-asked. Must be a stable reference
   * (module constant or undefined) — it's an effect dependency.
   */
  excludeKeys?: readonly string[];
};

// Maps a started program-unit instance into the flat question shape the survey
// renderer consumes (mirrors Sparta's AssignedScreenerContainer.memoizedQuestions).
function toContent(instance: ProgramUnitInstance): ScreenerContent {
  const questionGroup = instance.programUnit?.contentCards?.[0]?.questionGroup;
  const questions: SurveyQuestion[] = (questionGroup?.questions ?? []).map((question, index) => ({
    key: question.uuid,
    order: index + 1,
    text: question.text,
    additionalInfo: question.additionalInfo,
    questionType: question.questionType,
    choices: question.answerChoices.map((choice) => ({
      text: choice.description || choice.text,
      surveyChoiceUrn: choice.resourceUri,
    })),
  }));

  return {
    programUnitInstanceUri: instance.resourceUri,
    questionGroupUri: questionGroup?.resourceUri ?? '',
    questions,
    internalName: instance.programUnit?.internalName ?? '',
  };
}

// Consumes the assigned-screeners list one at a time: fetches it, takes the
// FIRST entry, and starts its program-unit instance to get the questions. The
// backend re-sorts after each answer, so the screen calls `next()` post-submit
// to reload — until the list comes back empty (`isExhausted`).
export function useAssignedScreener(options?: UseAssignedScreenerOptions): UseAssignedScreener {
  const excludeKeys = options?.excludeKeys;
  const [content, setContent] = useState<ScreenerContent | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isExhausted, setIsExhausted] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [token, setToken] = useState(0);

  const next = useCallback(() => setToken((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);
        setError(null);
        const screeners = await fetchAssignedScreeners(undefined, { excludeKeys });
        const first = screeners[0];
        if (!first) {
          if (!cancelled) {
            setIsExhausted(true);
            setIsLoading(false);
          }
          return;
        }
        const instance = await createProgramUnitInstance(first.uuid);
        if (cancelled) return;
        if (!instance) {
          // Nothing to render — treat like exhaustion so the screen advances.
          setIsExhausted(true);
          setIsLoading(false);
          return;
        }
        setContent(toContent(instance));
        setIsLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, excludeKeys]);

  return { content, isLoading, isExhausted, error, next };
}
